import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as Icon from 'react-feather';

import CallexLoader from '../../../components/CallexLoader';
import FormSelect from '../../../components/form/FormSelect';
import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';

import {
  processAllOptionGet,
  userOptionGetByProcess
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import { getChatReport } from '../../../store/slices/tenantSlices/chatSlice';
import { supervisorOptionGet } from '../../../store/slices/tenantSlices/supervisorSlice';
import ChatReportInfo from './ChatReportInfo';
import PreviewModal from '../../Chat/PreviewModal';
import { useTimezone } from '../../../store/slices/commonSlice';
import { formatPayloadDate } from '../../../utils/common';
import { toZonedTime } from 'date-fns-tz';

const ChatReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [infoDetails, setInfoDetails] = useState(null);
  const [isPreviewUrl, setIsPreviewUrl] = useState(null);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    processId: '',
    to_userId: '',
    from_userId: '',
    type: ''
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required'),
    processId: Yup.string().required('Process is required'),
    to_userId: Yup.string().required('to user is required'),
    from_userId: Yup.string().required('from user is required'),
    type: Yup.string().required('Type is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(getChatReport(payload)).unwrap();
      if (response?.data) {
        setReportData({
          data: [response.data],
          header: response.header || []
        });
      } else {
        setReportData({
          data: [],
          header: []
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleBlur, setFieldValue, handleSubmit, handleChange, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const id = [values?.processId];
      const serializedProcessId = `[${[id].join(',')}]`;
      const response = await dispatch(
        userOptionGetByProcess({ processIds: serializedProcessId })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: val.id
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  const getSupervisorOption = async () => {
    try {
      const response = await dispatch(supervisorOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.username || '-',
          value: val.id
        }));
        setSupervisorOptions(options);
      } else {
        setSupervisorOptions([]);
      }
    } catch (error) {
      setSupervisorOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessOption();
    getSupervisorOption();
  }, []);

  useEffect(() => {
    setFieldValue('from_userId', '');
    setFieldValue('to_userId', '');
    if (values?.processId !== '') {
      getUserOption();
    } else {
      setUserOptions([]);
    }
  }, [values?.processId, values?.type]);

  useEffect(() => {
    setFieldValue('from_userId', '');
    setFieldValue('to_userId', '');
  }, [values?.type]);

  return (
    <div>
      <AdminHeader
        title="Chat Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  name="start_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  maxDate={today}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  name="end_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  maxDate={today}
                  minDate={values.start_date}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.processId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="processId"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Type"
                  placeholder="Select type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="type"
                  options={[
                    { label: 'Agent to Agent', value: 'agent-to-agent' },
                    { label: 'Supervisor to Agent', value: 'supervisor-to-agent' }
                  ]}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label={values?.type === 'supervisor-to-agent' ? 'From Supervisor' : 'From user'}
                  placeholder={
                    values?.type === 'supervisor-to-agent'
                      ? 'Select from supervisor'
                      : 'Select from user'
                  }
                  value={values.from_userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="from_userId"
                  options={[
                    ...(values?.type === 'supervisor-to-agent' ? supervisorOptions : userOptions)
                  ]}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="To user"
                  placeholder="Select to user"
                  value={values.to_userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="to_userId"
                  options={userOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative mb-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                  <th className="text-center" style={{ width: '90px' }}>
                    Info
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header?.map((value, idx) => {
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                      <td>
                        <div className="d-flex justify-content-center align-items-center">
                          <Icon.Info
                            size={16}
                            className="cursor-pointer text-primary"
                            onClick={() =>
                              setInfoDetails({
                                ...val,
                                to_userId: values?.to_userId,
                                type: values?.type
                              })
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      {infoDetails && (
        <ChatReportInfo
          isOpen={!!infoDetails}
          handleCancel={() => setInfoDetails(null)}
          details={infoDetails}
          setIsPreviewUrl={setIsPreviewUrl}
        />
      )}
      {isPreviewUrl && (
        <PreviewModal
          isOpen={isPreviewUrl}
          url={isPreviewUrl}
          handleCancel={() => setIsPreviewUrl(null)}
        />
      )}
    </div>
  );
};

export default ChatReport;
